import React, { useEffect, useMemo, useRef } from "react";
import parse, { attributesToProps, domToReact, Element, type DOMNode, type HTMLReactParserOptions } from "html-react-parser";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { CONSENT_EVENT } from "@/lib/cookieConsent";
import { pushToDataLayer, trackFormSubmit } from "@/lib/analytics";
import { initializePage } from "./interactions";
import { initHallucinationFeed } from "./hallucinationFeed";
import { articles, isScheduled } from "@/data/articles";
import { resolveImportedLink } from "./routes";
import ContactEmbed from "./ContactEmbed";
import { RetainedLayoutContext } from "./RetainedLayoutContext";
import manifest from "./manifest.json";
import "./site.css";
import "./overrides.css";

const pages = import.meta.glob<string>("./pages/*.html", { query: "?raw", import: "default", eager: true });

function updateProductNavigation(source: string) {
  return source.replace(/<header class="site-header">[\s\S]*?<\/header>|<footer class="site-footer">[\s\S]*?<\/footer>/g, navigation => navigation
    .replace(/href="index\.html#engine"(?=[^>]*>(?:<strong>)?(?:The TrustScale Engine|Prism)<)/g, 'href="prism.html"')
    .replace(/The TrustScale Engine/g, "Prism")
    .replace(/Governed recursive self-improvement/gi, "Governed Self-Improvement")
    .replace('<span class="sol-tag">The full loop</span>', '<span class="sol-tag">Governed Self-Improvement</span>')
    .replace('<span class="sol-tag">Human in the loop</span>', '<span class="sol-tag">Expert humans in the loop</span>')
    .replace(/(<div class="sol-pane on" data-p="engine">)[\s\S]*?<\/div>/, '$1<span class="sol-h">Product components</span><div style="padding:6px 0 10px"><strong style="display:block">Nuance + Argus + Humanus</strong><span class="sol-tag">One unified platform</span></div><a class="sol-ov" data-track="nav_click" href="governed-self-improvement.html">Governed Self-Improvement</a></div>')
    .replace(/(<a class="sol-ov" href="humanus.html">)/, '<a data-track="nav_click" href="humanus.html"><strong>Private data services</strong></a>$1')
    .replace(/(<a href="world-model-training.html">World model training<\/a>)/, '$1<a href="humanus.html">Private data services</a>'));
}

function setMeta(key: string, content: string, property = false) {
  const attribute = property ? "property" : "name";
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) { element = document.createElement("meta"); element.setAttribute(attribute, key); document.head.appendChild(element); }
  element.content = content;
}

/** Children retain published content while reusing the redesign's header and footer. */
export default function ImportedPage({ page, children: retainedContent }: { page: keyof typeof manifest; children?: React.ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const { locale = "en" } = useParams();
  const { resolvedTheme: theme, setTheme } = useTheme();
  const location = useLocation();
  const source = updateProductNavigation(pages[`./pages/${page}.html`] ?? "");
  const isRetainedLayout = retainedContent !== undefined;
  const content = isRetainedLayout
    ? `${source.match(/<a class="skip"[^>]*>.*?<\/a>/s)?.[0] ?? ""}${source.match(/<header class="site-header">.*?<\/header>/s)?.[0] ?? ""}<main id="main" data-retained-content></main>${source.match(/<footer class="site-footer">.*?<\/footer>/s)?.[0] ?? ""}`
    : source;
  const toggleRef = useRef(() => {});
  toggleRef.current = () => setTheme(theme === "dark" ? "light" : "dark");

  useEffect(() => {
    if (isRetainedLayout) return;
    const info = manifest[page];
    document.title = info.title;
    setMeta("description", info.description);
    setMeta("robots", "index, follow");
    setMeta("og:title", info.title, true);
    setMeta("og:description", info.description, true);
    setMeta("og:type", "website", true);
    setMeta("twitter:title", info.title);
    setMeta("twitter:description", info.description);
    setMeta("twitter:card", "summary_large_image");
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = `https://trustscale.ai${location.pathname}`;
    setMeta("og:url", canonical.href, true);
    document.querySelectorAll('script[type="application/ld+json"]').forEach(script => script.remove());
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "WebPage", name: info.title, description: info.description, url: canonical.href, publisher: { "@type": "Organization", name: "TrustScale", url: "https://trustscale.ai" } });
    document.head.appendChild(schema);
    return () => schema.remove();
  }, [page, location.pathname, isRetainedLayout]);

  useEffect(() => {
    const element = root.current;
    if (!element) return;
    const cleanup = initializePage(element, () => toggleRef.current());
    initHallucinationFeed(element);
    articles.filter(a => isScheduled(a)).forEach(a => {
      element.querySelectorAll<HTMLAnchorElement>(`a[href$="/news/${a.slug}"]`).forEach(link => {
        if (link.closest(".site-header, .site-footer")) return;
        link.removeAttribute("href");
        link.setAttribute("aria-hidden", "true");
        link.style.filter = "blur(8px)";
        link.style.opacity = "0.6";
        link.style.pointerEvents = "none";
        link.style.userSelect = "none";
      });
    });
    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView());
    }
    return cleanup;
  }, [page, location.pathname]);

  useEffect(() => {
    root.current?.querySelectorAll(".theme-toggle").forEach(button => button.setAttribute("aria-label", theme === "dark" ? "Switch to light mode" : "Switch to dark mode"));
  }, [theme, page]);

  const rendered = useMemo(() => {
    const options: HTMLReactParserOptions = {
      replace(node) {
        if (!(node instanceof Element)) return;
        if (node.name === "script") return <></>;
        const attrs = node.attribs;
        const children = () => domToReact(node.children as DOMNode[], options);
        if ("data-retained-content" in attrs) {
          return <main id="main" className="retained-content px-6 py-12 md:py-16"><RetainedLayoutContext.Provider value={true}>{retainedContent}</RetainedLayoutContext.Provider></main>;
        }
        if (node.name === "form" && "data-proto" in attrs) {
          if (attrs["data-form"] === "demo_request") return <ContactEmbed />;
          return <div className="form card document-request">
            <h2 className="h-3">Request documents</h2>
            <p className="body-2">Send your name, company, and the documents you need to our compliance team.</p>
            <Button asChild variant="ghost" className="btn btn-primary"><a href="mailto:compliance@trustscale.ai?subject=TrustScale%20security%20document%20request">Email compliance</a></Button>
          </div>;
        }
        if (node.name === "form" && attrs["data-hs-portal"]) {
          return <form {...attributesToProps(attrs)} onSubmit={submitGate}>{children()}</form>;
        }
        if (node.name === "button") {
          return <Button {...attributesToProps(attrs)} variant="ghost" className={`${attrs.class ?? ""} imported-control`}>{children()}</Button>;
        }
        if (node.name === "a") {
          const href = resolveImportedLink(attrs.href ?? "", locale);
          const props = attributesToProps({ ...attrs, href });
          const isButton = attrs.class?.split(" ").includes("btn");
          const inner = attrs["data-cookie-preferences"] ? <a {...props} onClick={event => { event.preventDefault(); window.dispatchEvent(new Event(CONSENT_EVENT)); }}>{children()}</a>
            : href.startsWith("/") ? <Link {...props} to={href} target={undefined}>{children()}</Link> : <a {...props}>{children()}</a>;
          return isButton ? <Button asChild variant="ghost" className="imported-control">{inner}</Button> : inner;
        }
      },
    };
    return parse(content, options);
  }, [content, locale, retainedContent]);

  return <div ref={root} className="ts-site js" data-theme={theme === "dark" ? "dark" : "light"}>{rendered}</div>;
}

async function submitGate(event: React.FormEvent<HTMLFormElement>) {
  event.preventDefault();
  const form = event.currentTarget;
  const input = form.querySelector<HTMLInputElement>('input[type="email"]');
  const message = form.querySelector<HTMLElement>(".form-msg");
  const button = form.querySelector<HTMLButtonElement>('button[type="submit"], button');
  const email = input?.value.trim();
  if (!email || !input?.checkValidity()) { input?.reportValidity(); return; }
  const portal = form.dataset.hsPortal;
  const id = form.dataset.hsForm;
  if (!portal || !id) { if (message) message.textContent = "Please contact contact@trustscale.ai for access."; return; }
  if (button) button.disabled = true;
  if (message) { message.setAttribute("role", "status"); message.textContent = "Submitting…"; }
  try {
    const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portal}/${id}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fields: [{ objectTypeId: "0-1", name: "email", value: email }], context: { pageUri: location.href, pageName: document.title } }),
    });
    if (!response.ok) throw new Error("Submission unavailable");
    const gate = form.closest(".ucg");
    gate?.classList.add("open");
    const body = gate?.querySelector<HTMLElement>(".ucg-body");
    body?.removeAttribute("inert"); body?.removeAttribute("aria-hidden");
    body?.querySelector<HTMLElement>("a")?.focus({ preventScroll: true });
    trackFormSubmit("use_case_access", form.dataset.loc ?? "use_case");
    pushToDataLayer({ event: "use_case_access", hs_status: "sent" });
    if (message) message.textContent = "Thank you. Your use case is ready.";
  } catch {
    if (message) { message.setAttribute("role", "alert"); message.textContent = "We couldn’t submit your request. Please try again."; }
  } finally { if (button) button.disabled = false; }
}